const LOGO = "https://customer-assets-v7afamib.emergentagent.net/job_archi-interior-hub/artifacts/jmvnezz2_image.png";

const esc = (s) =>
  String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const STATUS_LABEL = { received: "Received", pending: "Pending", na: "N/A" };

// Builds a printable one-page Site Constraints Report and opens the browser's
// print-to-PDF dialog. Mirrors printProject.js (sandboxed Blob URL, escaped values).
export function printChecklistReport({ project, checklist }) {
  const cd = project.client_details || {};
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const clientLogo = cd.logo_path ? `${backend}/api/files/${cd.logo_path}` : null;
  const preparedOn = new Date().toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" });
  const items = (checklist?.items || []);
  const received = items.filter((i) => i.status === "received");
  const missing = items.filter((i) => i.status === "pending");
  const withNotes = items.filter((i) => (i.notes || "").trim());

  const attRef = (a) =>
    a.type === "link"
      ? `<a href="${esc(a.url)}">${esc(a.name || a.url)}</a>`
      : esc(a.original_filename || "file");

  const receivedRows = received.length
    ? received
        .map((i) => {
          const refs = (i.attachments || []).map(attRef).join(", ") || "—";
          return `<tr>
            <td class="lbl">${esc(i.label)}</td>
            <td>${refs}</td>
            <td>${i.notes ? esc(i.notes) : "—"}</td>
          </tr>`;
        })
        .join("")
    : `<tr><td colspan="3" class="muted">No documents marked received yet.</td></tr>`;

  const constraints = withNotes.length
    ? withNotes
        .map(
          (i) =>
            `<div class="con"><div class="con-h">${esc(i.label)} <span class="con-s con-${i.status}">${esc(STATUS_LABEL[i.status] || "")}</span></div><p>${esc(i.notes)}</p></div>`
        )
        .join("")
    : `<p class="muted">No site-constraint notes recorded yet.</p>`;

  const missingBlock = missing.length
    ? `<div class="missing">
        <div class="missing-h">⚠ MISSING DOCUMENTS · ${missing.length}</div>
        <div class="missing-list">${missing.map((i) => `<span class="mbadge">${esc(i.label)}</span>`).join("")}</div>
      </div>`
    : `<div class="allgood">✓ All applicable documents received</div>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(project.name)} — Site Constraints Report</title>
  <style>
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #18181b; margin: 32px; }
    .head { display:flex; align-items:center; gap:16px; border-bottom:2px solid #18181b; padding-bottom:16px; margin-bottom:20px; }
    .head img { height:52px; width:auto; }
    .head .clogo { height:44px; margin-left:auto; object-fit:contain; }
    .brand { font-size:11px; letter-spacing:.2em; text-transform:uppercase; color:#71717a; }
    h1 { font-size:26px; margin:0 0 6px; letter-spacing:-0.02em; }
    .meta { font-size:13px; color:#52525b; margin:2px 0; }
    .meta strong { color:#18181b; }
    h2 { font-size:13px; text-transform:uppercase; letter-spacing:.12em; color:#71717a; margin:26px 0 10px; border-bottom:1px solid #e4e4e7; padding-bottom:6px; }
    table { width:100%; border-collapse:collapse; font-size:12px; }
    th { text-align:left; font-size:9px; text-transform:uppercase; letter-spacing:.1em; color:#a1a1aa; padding:6px 8px; border-bottom:1px solid #e4e4e7; }
    td { padding:8px; border-bottom:1px solid #f4f4f5; vertical-align:top; }
    td.lbl { font-weight:600; white-space:nowrap; }
    .muted { color:#a1a1aa; }
    a { color:#2563eb; text-decoration:none; word-break:break-all; }
    .con { margin-bottom:12px; page-break-inside:avoid; }
    .con-h { font-size:12px; font-weight:700; }
    .con-h .con-s { font-size:9px; font-weight:600; padding:1px 6px; border-radius:2px; margin-left:6px; text-transform:uppercase; letter-spacing:.05em; }
    .con-received { background:#dcfce7; color:#15803d; }
    .con-pending { background:#fef9c3; color:#a16207; }
    .con-na { background:#f4f4f5; color:#71717a; }
    .con p { margin:4px 0 0; font-size:12px; color:#3f3f46; white-space:pre-wrap; }
    .missing { border:2px solid #e11d48; border-radius:4px; padding:14px 16px; margin-top:8px; background:#fff1f2; }
    .missing-h { color:#e11d48; font-weight:800; font-size:13px; letter-spacing:.05em; margin-bottom:10px; }
    .mbadge { display:inline-block; background:#e11d48; color:#fff; font-size:11px; font-weight:600; padding:3px 9px; border-radius:2px; margin:0 6px 6px 0; }
    .allgood { border:2px solid #059669; border-radius:4px; padding:14px 16px; background:#ecfdf5; color:#047857; font-weight:700; font-size:13px; }
    .foot { margin-top:28px; font-size:10px; color:#a1a1aa; border-top:1px solid #e4e4e7; padding-top:10px; }
    @media print { body { margin:16px; } }
  </style></head><body>
    <div class="head">
      <img src="${LOGO}" alt="TWP co" />
      <div class="brand">TWP co · Site Constraints Report</div>
      ${clientLogo ? `<img class="clogo" src="${esc(clientLogo)}" alt="client" />` : ""}
    </div>
    <h1>${esc(project.name)}</h1>
    ${project.address ? `<p class="meta"><strong>Address:</strong> ${esc(project.address)}</p>` : ""}
    <p class="meta"><strong>Client:</strong> ${esc(project.client || cd.name || project.builder || "—")}${cd.email ? ` · ${esc(cd.email)}` : ""}${cd.phone ? ` · ${esc(cd.phone)}` : ""}</p>
    <p class="meta"><strong>Prepared on:</strong> ${esc(preparedOn)}</p>

    <h2>Documents Received</h2>
    <table>
      <thead><tr><th>Document</th><th>Attachment / Link</th><th>Notes</th></tr></thead>
      <tbody>${receivedRows}</tbody>
    </table>

    <h2>Site Constraints Summary</h2>
    ${constraints}

    <h2>Missing Documents</h2>
    ${missingBlock}

    <div class="foot">Generated ${new Date().toLocaleString()} · Pre-design document review · TWP co</div>
  </body></html>`;

  const blob = new Blob([html], { type: "text/html" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank");
  if (!w) {
    URL.revokeObjectURL(url);
    return false;
  }
  w.addEventListener("load", () => {
    w.focus();
    setTimeout(() => {
      try { w.print(); } catch (e) { console.error("Print failed:", e); }
      URL.revokeObjectURL(url);
    }, 350);
  });
  return true;
}
